// Visual pages for one continuous note.
//
// These figures are presentation only. They never enter a note's text, a request,
// localStorage, or the database. The page length follows the rendered paper width,
// so wrapping, browser zoom, fonts, and viewport changes are allowed to move every
// boundary. The textarea and the rendered Markdown remain one continuous document.

/** A restrained portrait sheet: US Letter's familiar width-to-height proportion. */
export const VISUAL_PAGE_RATIO = 11 / 8.5;

/** The visual length of a sheet at this rendered paper width. */
export function visualPageLength(width: number): number {
  if (!Number.isFinite(width) || width <= 0) return 0;
  return Math.max(1, Math.round(width * VISUAL_PAGE_RATIO));
}

/** At least one sheet, even for a new note with nothing in it yet. */
export function visualPageCount(contentHeight: number, pageLength: number): number {
  if (!Number.isFinite(pageLength) || pageLength <= 0) return 1;
  const height = Number.isFinite(contentHeight) ? Math.max(0, contentHeight) : 0;
  return Math.max(1, Math.ceil(height / pageLength));
}

/** The boundaries after sheet one, measured from the paper's top edge. */
export function visualPageDividers(total: number, pageLength: number): number[] {
  if (!Number.isFinite(pageLength) || pageLength <= 0) return [];
  const count = Math.max(1, Math.floor(total));
  return Array.from({ length: count - 1 }, (_, index) => (index + 1) * pageLength);
}

/** Keep a requested sheet inside the visual document. */
export function clampVisualPage(page: number, total: number): number {
  const last = Math.max(1, Math.floor(total));
  if (!Number.isFinite(page)) return 1;
  return Math.min(last, Math.max(1, Math.floor(page)));
}

/**
 * Which visual sheet contains the reading position.
 *
 * A little of the viewport is looked through rather than using its very top.
 * That keeps the indicator on the sheet somebody is reading when a boundary is
 * just below a sticky browser edge. It changes no scrolling and no content.
 */
export function visualPageAt(
  offsetWithinPaper: number,
  pageLength: number,
  total: number,
  viewportHeight = 0,
): number {
  if (!Number.isFinite(pageLength) || pageLength <= 0) return 1;
  const lookAhead = Math.min(
    pageLength * 0.2,
    Math.max(0, Number.isFinite(viewportHeight) ? viewportHeight : 0) * 0.2,
  );
  const offset = Math.max(0, Number.isFinite(offsetWithinPaper) ? offsetWithinPaper : 0);
  return clampVisualPage(Math.floor((offset + lookAhead) / pageLength) + 1, total);
}
